import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { webhooksApi } from '../api/client';
import { T } from '../components/ui';

const ALL_EVENTS = [
  'alert.created', 'alert.severity_changed',
  'recall.activated', 'recall.scope_updated',
  'score.changed', 'lot.flagged',
  'dppApi.published', 'iotApi.threshold_exceeded',
];

export default function WebhooksPage() {
  const { user } = useAuth();
  const { data: endpoints, loading, refetch } = useApi(() => webhooksApi.list(), [user]);

  const [showCreate, setShowCreate]   = useState(false);
  const [selectedId, setSelectedId]   = useState<string | null>(null);
  const { data: deliveries, refetch: refetchDeliveries } = useApi(
    () => selectedId ? webhooksApi.deliveries(selectedId) : Promise.resolve(null),
    [selectedId]
  );

  const [newUrl, setNewUrl]           = useState('');
  const [newDesc, setNewDesc]         = useState('');
  const [newEvents, setNewEvents]     = useState<string[]>(['alert.created', 'recall.activated']);
  const [createdSecret, setCreatedSecret] = useState('');
  const [creating, setCreating]       = useState(false);
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; status_code: number | null; duration_ms: number }>>({});
  const [testing, setTesting]         = useState('');
  const [error, setError]             = useState('');

  const toggleEvent = (ev: string) =>
    setNewEvents(evs => evs.includes(ev) ? evs.filter(e => e !== ev) : [...evs, ev]);

  const createEndpoint = async () => {
    if (!newUrl || newEvents.length === 0) return;
    setCreating(true); setError('');
    try {
      const res = await webhooksApi.create({ url: newUrl, events: newEvents, description: newDesc || undefined });
      setCreatedSecret(res.secret);
      setNewUrl(''); setNewDesc(''); setNewEvents(['alert.created', 'recall.activated']);
      refetch?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create endpoint');
    } finally {
      setCreating(false);
    }
  };

  const testEndpoint = async (id: string) => {
    setTesting(id);
    try {
      const res = await webhooksApi.test(id);
      setTestResults(r => ({ ...r, [id]: res }));
    } finally {
      setTesting('');
    }
  };

  const toggleActive = async (id: string, currentlyActive: boolean) => {
    await webhooksApi.update(id, { is_active: !currentlyActive });
    refetch?.();
  };

  const deleteEndpoint = async (id: string) => {
    if (!confirm('Delete this webhook endpoint?')) return;
    await webhooksApi.delete(id);
    if (selectedId === id) setSelectedId(null);
    refetch?.();
  };

  const redeliverEvent = async (deliveryId: string) => {
    try {
      await webhooksApi.redeliver(deliveryId);
      setTimeout(() => refetchDeliveries?.(), 1000);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Redeliver failed');
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '9px 12px',
    background: T.panel, border: `1px solid ${T.border}`,
    borderRadius: 8, color: T.text, fontSize: 13,
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: 900, display: 'grid', gridTemplateColumns: selectedId ? '1fr 380px' : '1fr', gap: 24 }}>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>Webhooks</h1>
            <p style={{ fontSize: 13, color: T.sub, margin: 0 }}>
              Push real-time events to your systems via HMAC-signed HTTP POST
            </p>
          </div>
          <button
            onClick={() => { setShowCreate(v => !v); setCreatedSecret(''); setError(''); }}
            style={{ padding: '9px 18px', background: T.copper, border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            + Add endpoint
          </button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
            {createdSecret ? (
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.sage, marginBottom: 8 }}>✓ Endpoint created</div>
                <div style={{ fontSize: 13, color: T.text, marginBottom: 6 }}>
                  Save your signing secret — it will not be shown again:
                </div>
                <div style={{
                  background: T.card, borderRadius: 8, padding: '10px 14px',
                  fontFamily: 'monospace', fontSize: 12, color: T.copper,
                  wordBreak: 'break-all', marginBottom: 16,
                }}>
                  {createdSecret}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(createdSecret); }}
                  style={{ padding: '6px 14px', background: T.border, border: 'none', borderRadius: 6, fontSize: 12, color: T.text, cursor: 'pointer', marginRight: 10 }}
                >
                  Copy secret
                </button>
                <button
                  onClick={() => { setShowCreate(false); setCreatedSecret(''); }}
                  style={{ padding: '6px 14px', background: T.copper, border: 'none', borderRadius: 6, fontSize: 12, color: '#fff', cursor: 'pointer' }}
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 600, color: T.text }}>New endpoint</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: T.sub, marginBottom: 5 }}>URL (HTTPS required)</label>
                    <input style={inputStyle} placeholder="https://api.yourapp.com/webhooks/tracefull" value={newUrl} onChange={e => setNewUrl(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: T.sub, marginBottom: 5 }}>Description (optional)</label>
                    <input style={inputStyle} placeholder="e.g. Production alert handler" value={newDesc} onChange={e => setNewDesc(e.target.value)} />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: T.sub, marginBottom: 8 }}>Events to subscribe</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {ALL_EVENTS.map(ev => (
                        <label key={ev} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.text, cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={newEvents.includes(ev)}
                            onChange={() => toggleEvent(ev)}
                            style={{ cursor: 'pointer' }}
                          />
                          {ev}
                        </label>
                      ))}
                    </div>
                  </div>
                  {error && <div style={{ fontSize: 12, color: T.crimson }}>{error}</div>}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => setShowCreate(false)} style={{ padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7, color: T.sub, fontSize: 13, cursor: 'pointer' }}>
                      Cancel
                    </button>
                    <button onClick={createEndpoint} disabled={creating || !newUrl || newEvents.length === 0} style={{ padding: '8px 20px', background: T.copper, border: 'none', borderRadius: 7, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      {creating ? 'Creating…' : 'Create endpoint'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Endpoint list */}
        {loading ? (
          <div style={{ color: T.sub, fontSize: 13, textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : !endpoints || endpoints.length === 0 ? (
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, padding: 40, textAlign: 'center', color: T.sub, fontSize: 13 }}>
            No webhook endpoints yet. Add one above.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {endpoints.map(ep => (
              <div
                key={ep.id}
                style={{
                  background: selectedId === ep.id ? T.lift : T.panel,
                  border: `1px solid ${selectedId === ep.id ? T.copper + '40' : T.border}`,
                  borderRadius: 12, padding: '16px 20px', cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
                onClick={() => setSelectedId(selectedId === ep.id ? null : ep.id)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 8, height: 8, borderRadius: '50%',
                    background: ep.is_active ? T.sage : T.dim,
                    marginTop: 5, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ep.url}
                    </div>
                    {ep.description && (
                      <div style={{ fontSize: 11, color: T.sub, marginTop: 2 }}>{ep.description}</div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                      {ep.events.map(ev => (
                        <span key={ev} style={{ fontSize: 10, padding: '2px 8px', background: `${T.sapphire}20`, color: T.sapphire, borderRadius: 20 }}>
                          {ev}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {testResults[ep.id] && (
                      <span style={{ fontSize: 11, color: testResults[ep.id].success ? T.sage : T.crimson }}>
                        {testResults[ep.id].success ? '✓' : '✗'} {testResults[ep.id].status_code || 'err'}
                      </span>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); testEndpoint(ep.id); }}
                      disabled={testing === ep.id}
                      style={{ padding: '4px 10px', background: T.sapphire, border: 'none', borderRadius: 6, color: '#fff', fontSize: 11, cursor: 'pointer' }}
                    >
                      {testing === ep.id ? '…' : 'Test'}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); toggleActive(ep.id, ep.is_active); }}
                      style={{ padding: '4px 10px', background: T.border, border: 'none', borderRadius: 6, color: T.text, fontSize: 11, cursor: 'pointer' }}
                    >
                      {ep.is_active ? 'Pause' : 'Enable'}
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); deleteEndpoint(ep.id); }}
                      style={{ padding: '4px 10px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 6, color: T.sub, fontSize: 11, cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {ep.failure_count > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: T.amber }}>
                    ⚠ {ep.failure_count} consecutive failure{ep.failure_count > 1 ? 's' : ''}
                    {ep.disabled_at && ' — endpoint disabled'}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delivery log panel */}
      {selectedId && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Recent deliveries</div>
            <button onClick={() => setSelectedId(null)} style={{ background: 'transparent', border: 'none', color: T.sub, cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
          <div style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 12, overflow: 'hidden' }}>
            {!deliveries || deliveries.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: T.sub, fontSize: 13 }}>
                No deliveries yet
              </div>
            ) : (
              (deliveries as any[]).map((d: any, i: number) => (
                <div key={d.id} style={{
                  padding: '12px 16px',
                  borderBottom: i < deliveries.length - 1 ? `1px solid ${T.border}` : 'none',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: d.success ? T.sage : T.crimson, display: 'inline-block' }} />
                        <span style={{ fontSize: 12, color: T.text }}>{d.event_type}</span>
                        {d.status_code && (
                          <span style={{ fontSize: 11, color: d.success ? T.sage : T.crimson }}>
                            HTTP {d.status_code}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: T.dim }}>
                        {new Date(d.delivered_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        {' · '}{d.duration_ms}ms{d.attempt > 1 && ` · attempt ${d.attempt}`}
                      </div>
                      {d.error_message && (
                        <div style={{ fontSize: 11, color: T.crimson, marginTop: 2, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.error_message}
                        </div>
                      )}
                    </div>
                    {!d.success && (
                      <button
                        onClick={() => redeliverEvent(d.id)}
                        style={{ padding: '3px 8px', background: T.border, border: 'none', borderRadius: 5, color: T.sub, fontSize: 10, cursor: 'pointer', flexShrink: 0 }}
                      >
                        Retry
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
